import React from 'react';

import { Text } from '@change/ds-core-ui';
import { TikTokIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';

import { SourceIconStack } from '#src/app/pages/homepage/shared/components';

import type { SocialPostErrorCode } from '../../hooks/useSocialPostUrlForm';
import type { ErrorVariant } from '../ErrorMessage';
import { ERROR_VARIANT, ErrorMessage } from '../ErrorMessage';
import { HELPER_SOURCE_ITEMS } from '../SourceIcon';

// Container styling when no error is shown (the neutral TikTok helper state).
const DEFAULT_CONTAINER = 'bg-fill-information-weaker border-stroke-info-weak/50';

const CONTAINER_CLASS: Readonly<Record<ErrorVariant, string>> = {
	error: 'bg-fill-error-weaker border-stroke-error-weak',
	warning: 'bg-fill-warning-weaker border-stroke-warning-success-weak',
};

const NeutralHelper: React.FC<{ multiSource: boolean }> = ({ multiSource }) =>
	multiSource ? (
		<>
			<SourceIconStack items={HELPER_SOURCE_ITEMS} />
			<Text as="p" className="text-typography-weak">
				<Translate value="corgi.homepage.hero_section.input_helper_multi_source" />
				{/* the badge stack is decorative, so the accepted sources are named here for assistive tech */}
				<span className="sr-only" data-qa="source-icon-stack-sources">
					{' '}
					<Translate value="corgi.homepage.hero_section.input_helper_multi_source_sources" />
				</span>
			</Text>
		</>
	) : (
		<>
			<div className="bg-fill-strong border-stroke-inverse-strong elevation-low flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2">
				<TikTokIcon className="text-icon-inverse-strong" size="x-small" />
			</div>
			<Text as="p" className="text-typography-weak">
				<Translate value="corgi.homepage.hero_section.input_helper_tiktok" />
			</Text>
		</>
	);

export const FormHelper: React.FC<Props> = ({ className, error, multiSource = false }) => {
	const errorVariant = error ? ERROR_VARIANT[error] : undefined;

	return (
		<div
			data-qa="social-post-callout"
			className={cn(
				'rounded-large mx-auto -mt-4 flex max-w-[95%] items-center gap-2 border px-5 pt-6 pb-3',
				errorVariant ? CONTAINER_CLASS[errorVariant] : DEFAULT_CONTAINER,
				errorVariant && 'items-start',
				className,
			)}
		>
			{error ? (
				<ErrorMessage appearance="callout" error={error} multiSource={multiSource} />
			) : (
				<NeutralHelper multiSource={multiSource} />
			)}
		</div>
	);
};

type Props = Readonly<{
	className?: string;
	error?: SocialPostErrorCode;
	/** Word the helper and errors for any allowlisted link, not only TikTok. */
	multiSource?: boolean;
}>;
